import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import {
  createTagRegexesInvoice,
  createTagRegexesSet,
  createTagRegexWithSet,
  getTaxRegexesByType,
  updateTagRegexesInvoice,

} from "../../docuclipper/api";
import {
  DocType,
  TagInvoiceRule,
  TagRegexes,
  TagRegexesSets,
  TagRegexesType,

} from "../../docuclipper/DocuclipperTypes";
import { createAlert } from "./Alerts";
import { deleteTagRegexes } from '../../docuclipper/api';
import { applyRunInvoiceRules } from "./InvoiceManager";

const initialState: {
  loading: boolean;
  tagRegexesInvoice: TagRegexes[];
  TagRegexesInvoiceSets: TagRegexesSets[];

} = {
  loading: false,
  tagRegexesInvoice: [],
  TagRegexesInvoiceSets: [],
};

const slice = createSlice({
  name: "TagRegexesInvoice",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    updateTagRegexesInvoice2(state, action: PayloadAction<{ id; jsonRules }>) {
      state.tagRegexesInvoice = state.tagRegexesInvoice.map((x) =>
        x.id === action.payload.id
          ? { ...x, jsonRules: action.payload.jsonRules }
          : x
      );
    },
    addTagRegexesInvoice(state, action: PayloadAction<TagRegexes>) {
      state.tagRegexesInvoice = [...state.tagRegexesInvoice, action.payload];
    },
    addTagRegexesInvoiceSet(state, action: PayloadAction<TagRegexesSets>) {
      state.TagRegexesInvoiceSets = [...state.TagRegexesInvoiceSets, action.payload];
    },
    setTagRegexesInvoice(state, action: PayloadAction<TagRegexes[]>) {
      state.tagRegexesInvoice = action.payload;
    },
    setTagRegexesInvoiceSets(state, action: PayloadAction<TagRegexesSets[]>) {
      state.TagRegexesInvoiceSets = action.payload;
    },
  },
});

export const {
  setLoading,
  updateTagRegexesInvoice2,
  setTagRegexesInvoice,
  setTagRegexesInvoiceSets,
  addTagRegexesInvoice,
  addTagRegexesInvoiceSet,
} = slice.actions;


export const fetchTagRegexesInvoice = (type: TagRegexesType, integrationId: number | null, docType: DocType) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (state.TagRegexesInvoice.loading) {
    return;
  }
  dispatch(setLoading(true));

  try {
    const {
      tagRegexes: tagRegexesInvoice,
      tagRegexesSets: tagRegexesInvoiceSets
    } = await getTaxRegexesByType(type, integrationId, docType);

    dispatch(setTagRegexesInvoice(tagRegexesInvoice));
    dispatch(setTagRegexesInvoiceSets(tagRegexesInvoiceSets));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};



export const updateTagRegexesInvoiceAction =
  (id, rule, integrationId, docType) => async (dispatch, getState) => {
    try {
      const state: ReduxState = getState();
      // Get transactionLineId from the selected transaction line
      const selectedTransactionLine = state.InvoiceManager.transactionLines.find(
        (tl) =>
          tl.documentId === state.InvoiceManager.documentId &&
          tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
      );
      const transactionLineId = selectedTransactionLine?.id
        ? (typeof selectedTransactionLine.id === "string"
          ? (isNaN(parseInt(selectedTransactionLine.id, 10))
            ? undefined
            : parseInt(selectedTransactionLine.id, 10))
          : selectedTransactionLine.id)
        : undefined;

      updateTagRegexesInvoice(id, rule)
        .then((res) => {
          dispatch(updateTagRegexesInvoice2({ id, jsonRules: res.jsonRules }));
          dispatch(
            createAlert({
              message: "Invoice rules updated successfully",
              timeout: 3000,
              type: "success",
            })
          );
          dispatch(applyRunInvoiceRules(integrationId, docType, transactionLineId));
        })
        .catch((err) => dispatch(createAlert({
          message: err.message || "Error updating invoice rules. Please try again.",
          timeout: 0,
          type: "error",
        })));
    } catch (err) { }
  };



export const deleteTagRegexesInvoiceAction = (id, integrationId, docType) => async (dispatch, getState) => {
  try {
    const state: ReduxState = getState();
    // Get transactionLineId from the selected transaction line
    const selectedTransactionLine = state.InvoiceManager.transactionLines.find(
      (tl) =>
        tl.documentId === state.InvoiceManager.documentId &&
        tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
    );
    const transactionLineId = selectedTransactionLine?.id
      ? (typeof selectedTransactionLine.id === "string"
        ? (isNaN(parseInt(selectedTransactionLine.id, 10))
          ? undefined
          : parseInt(selectedTransactionLine.id, 10))
        : selectedTransactionLine.id)
      : undefined;

    deleteTagRegexes(id)
      .then(() => {
        dispatch(
          createAlert({
            message: "Invoice Rule deleted successfully",
            timeout: 3000,
            type: "success",
          })
        );
        dispatch(applyRunInvoiceRules(integrationId, docType, transactionLineId));

        dispatch(setTagRegexesInvoice(state.TagRegexesInvoice.tagRegexesInvoice.filter((x) => x.id !== id)));
        dispatch(setTagRegexesInvoiceSets(state.TagRegexesInvoice.TagRegexesInvoiceSets.filter((x) => x.tagRegexesId !== id)));
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error deleting invoice rule. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
}



export const addNewInvoiceGroup = (name, rule: TagInvoiceRule, integrationId, docType) => async (dispatch, getState) => {
  if (!name) {
    dispatch(
      createAlert({
        message: "Need to specify the invoice group name",
        timeout: 0,
        type: "error",
      })
    );
    return;
  }
  try {
    const state: ReduxState = getState();
    // Get transactionLineId from the selected transaction line
    const selectedTransactionLine = state.InvoiceManager.transactionLines.find(
      (tl) =>
        tl.documentId === state.InvoiceManager.documentId &&
        tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
    );
    const transactionLineId = selectedTransactionLine?.id
      ? (typeof selectedTransactionLine.id === "string"
        ? (isNaN(parseInt(selectedTransactionLine.id, 10))
          ? undefined
          : parseInt(selectedTransactionLine.id, 10))
        : selectedTransactionLine.id)
      : undefined;

    createTagRegexWithSet(name, `${rule.tag}`, "", "[]", rule, "invoiceRules")
      .then((rsp) => {
        dispatch(
          addTagRegexesInvoice({
            id: rsp.tagRegex.id,
            regexes: rsp.tagRegex.regexes,
            tag: rsp.tagRegex.tag,
            jsonRules: JSON.parse(rsp.tagRegex.jsonRules)
          })
        );
        dispatch(addTagRegexesInvoiceSet(rsp.tagRegexSet));
        dispatch(applyRunInvoiceRules(integrationId, docType, transactionLineId));
      })
      .catch((err) => dispatch(createAlert({
        message: err.message || "Error creating invoice group. Please try again.",
        timeout: 0,
        type: "error",
      })));
  } catch (err) { }
};

export default slice.reducer;
